/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  SearchSortOrder,
  SearchV2Sort,
  SearchV2SortField,
} from "~/generated/graphql";
import { SortDirection } from "~/components/SortSelector";

type Props = {
  field?: SearchV2SortField;
  order?: SearchSortOrder;
  updateQueryState: (params: SearchV2Sort & { page: string }) => void;
};

export function useEstateSortV2({ field, order, updateQueryState }: Props) {
  const sortParams = React.useMemo(() => {
    return {
      field,
      order,
    };
  }, [field, order]);

  const [sortField, setSortField] = React.useState<SearchV2SortField>(
    sortParams.field ?? SearchV2SortField.Cost
  );
  const [sortDirection, setSortDirection] = React.useState<SearchSortOrder>(
    sortParams.order ?? SearchSortOrder.Dsc
  );

  React.useEffect(() => {
    setSortField(sortParams.field ?? SearchV2SortField.Cost);
    setSortDirection(sortParams.order ?? SearchSortOrder.Dsc);
  }, [sortParams]);

  const onSortSelect = (filter: SearchV2SortField) => {
    const isDefaultDescending = [
      SearchV2SortField.Cost,
      SearchV2SortField.Forecast,
      SearchV2SortField.SavingsAmount,
    ].includes(filter);
    updateQueryState({
      field: filter,
      order: isDefaultDescending ? SearchSortOrder.Dsc : SearchSortOrder.Asc,
      page: "0",
    });
  };

  const onSortOrderChange = (direction: SortDirection) => {
    updateQueryState({
      field: sortField,
      order:
        direction === SortDirection.Ascending
          ? SearchSortOrder.Asc
          : SearchSortOrder.Dsc,
      page: "0",
    });
  };

  return {
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  };
}
